<template>
	<DialogControl v-model="openDialog" :attach="overlayTarget" max-width="600px" scrollable class="delivery-location-dialog">
		<v-card>
			<v-card-title class="d-flex ma-0">
				<v-icon class="" variant="solo" icon="store" color="primary"></v-icon>
				<span class="pa-2 text-primary">{{ t('Delivery Location') }}</span>
				<v-spacer />
				<v-btn color="primary" icon="close" variant="text" @click="close" />
			</v-card-title>
			<v-card-text class="ga-0">
				<div class="d-flex ga-2 pt-4">
					<v-text-field class="flex-1-0" v-model="searchText" density="compact" hide-details="auto" variant="outlined" prepend-inner-icon="search" :placeholder="t('Search')" clearable />
					<SearchSortByControl :select="false" :search="false" :items="sortOptions" v-model:sort-by="sortBy" v-model:sort-order="sortOrder" />
					<DeliveryLocationFiltersDialog ref="filtersDialogRef" needSlide :locations="baseLocations" @filters-changed="onFiltersChanged" />
				</div>

				<FilterChips :chips="filterChips" class="px-4" @select="onFilterChipSelect" @remove="removeFilterChip" @clear="resetFilters" />

				<v-virtual-scroll v-if="sortedLocations.length" :items="sortedLocations" item-height="48">
					<template v-slot:default="{ item }">
						<v-list-item class="text-display-small pa-2 mb-2 rounded-lg" border="theme-surface-variant thin" :active="selected?.id === item.id" color="primary" @click="selectLocation(item)">
							<template #prepend>
								<v-avatar color="primary" variant="tonal">
									<v-img v-if="avatarUrlFor(item)" :src="avatarUrlFor(item)" alt="" />
									<span v-else class="text-caption">{{ item.label[0] }}</span>
								</v-avatar>
							</template>

							<v-list-item-title class="text-headline-small">
								<v-icon v-if="isSharedDeliveryLocationId(item.id)" class="dl-shared-icon" size="x-small">share</v-icon>
								{{ item.label }}
							</v-list-item-title>
							<v-list-item-subtitle class="text-headline-small text-truncate">{{ item.addressLine }} - {{ item.postcode }} - {{ item.city }}</v-list-item-subtitle>

							<template #append>
								<v-tooltip :open-on-hover="false" open-on-click location="bottom">
									<template v-slot:activator="{ props: activatorProps }">
										<v-icon v-if="true" variant="solo" icon="info" color="primary" v-bind="activatorProps" />
									</template>
									<div style="list-style-type: none">
										<li v-if="item.label">
											<span class="text-body-medium font-weight-semibold">{{ t('Label') }}</span>
											:
											<span class="text-body-small">{{ item.label }}</span>
										</li>
										<li v-if="item.lastName">
											<span class="text-body-medium font-weight-semibold">{{ t('LastName') }}</span>
											:
											<span class="text-body-small">{{ item.firstName }} {{ item.lastName }}</span>
										</li>
										<li v-if="item.phoneNumber">
											<span class="text-body-medium font-weight-semibold">{{ t('Phone') }}</span>
											:
											<span class="text-body-small">{{ item.phoneNumber }}</span>
										</li>
										<li v-if="item.faxNumber">
											<span class="text-body-medium font-weight-semibold">{{ t('Fax') }}</span>
											:
											<span class="text-body-small">{{ item.faxNumber }}</span>
										</li>
										<li v-if="item.addressLine2">
											<span class="text-body-medium font-weight-semibold">{{ t('Address2') }}</span>
											:
											<span class="text-body-small">{{ item.addressLine2 }}</span>
										</li>
										<li v-if="item.reference">
											<span class="text-body-medium font-weight-semibold">{{ t('Ref') }}</span>
											:
											<span class="text-body-small">{{ item.reference }}</span>
										</li>
										<li v-if="item.addressComment">
											<span class="text-body-medium font-weight-semibold">{{ t('Comment') }}</span>
											:
											<span class="text-body-small">{{ item.addressComment }}</span>
										</li>
									</div>
								</v-tooltip>
							</template>
						</v-list-item>
					</template>
				</v-virtual-scroll>

				<v-empty-state v-else icon="location_off" :title="t('NoData')" />
			</v-card-text>

			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" @click="close" class="rounded-xl">{{ t('Cancel') }}</v-btn>
				<v-btn variant="tonal" :disabled="!selected" class="rounded-xl" @click="confirmSelection">{{ t('Select') }}</v-btn>
			</v-card-actions>
		</v-card>
	</DialogControl>
</template>

<script setup>
// #region Imports
import { ref, computed, onMounted, onBeforeUnmount, inject, watchEffect } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useTranslator } from '@/composables/useTranslator';
import { getAccountImageUrl } from '@/api/core/file.js';
import { isSharedDeliveryLocationId } from '@/utils/domain/sharedDeliveryLocations';
import DeliveryLocationFiltersDialog from '@/components/dialogs/DeliveryLocationFiltersDialog.vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import SearchSortByControl from '@/components/controls/SearchSortByControl.vue';
import FilterChips from '@/components/controls/FilterChips.vue';
import { eventBus } from '@/utils/core/eventBus.js';
// #endregion

// #region Refs & Vars
const t = useTranslator();
const ecosVM = iframeDataBridge.getEcosVM();
const deliveryLocations = ref(ecosVM?.deliveryLocations ?? []);
const overlayTarget = inject('overlayTarget', null);
const openDialog = ref(false);
const searchText = ref('');
const selected = ref(null);
const currentParams = ref({}); // { sender, authorizedDLT, dlId, locationIds }
const deliveryLocationModalType = ref(null);
const restrictedLocationIds = ref(null);
const avatarUrlByLocationId = ref({});
const sortBy = ref('label');
const sortOrder = ref('asc');
const filteredByDialog = ref(null); // last result emitted by DeliveryLocationFiltersDialog
const filtersDialogRef = ref(null);
const filterChips = ref([]); // active filters exposed by DeliveryLocationFiltersDialog (buildFilterChips shape), rendered via FilterChips
//#endregion

// #region Computed
const sortOptions = computed(() => [
	{ id: 'label', name: t('LabelAZ') },
	{ id: 'reference', name: t('Ref') },
]);

const sortedLocations = computed(() => {
	const key = sortBy.value;
	const direction = sortOrder.value === 'asc' ? 1 : -1;
	return [...filteredLocations.value].sort((a, b) => String(a?.[key] ?? '').localeCompare(String(b?.[key] ?? '')) * direction);
});

const baseLocations = computed(() => {
	const dltId = deliveryLocationModalType.value?.id;
	let list = deliveryLocations.value.filter((dl) => !dltId || dl.deliveryLocationTypeId === dltId);
	if (restrictedLocationIds.value) list = list.filter((dl) => restrictedLocationIds.value.has(dl.id));
	return list;
});

const searchRegExp = computed(() => {
	const val = searchText.value?.trim() ?? '';
	if (val.length > 0 && val.length < 2) return null; // same threshold as deliverylocation.js#searchDlText
	const pattern = val.split(' ').length > 1 ? '(.*' + val.split(' ').join(')(.*') + ')' : val;
	return new RegExp(pattern, 'i');
});

const filteredLocations = computed(() => {
	let list = filteredByDialog.value ?? baseLocations.value;
	const re = searchRegExp.value;
	if (re && searchText.value?.trim()) {
		list = list.filter((dl) => (dl.reference && re.test(dl.reference)) || (dl.label && re.test(dl.label)) || (dl.companyName && re.test(dl.companyName)) || (dl.addressLine && re.test(dl.addressLine)) || (dl.city && re.test(dl.city)));
	}
	return list;
});

// same logic as CatchmentComparisonProductCard: resolves the account image, with a fallback to the label letter
const accountsById = computed(() => new Map((ecosVM.accounts?.() || []).map((account) => [account.id, account])));

//#endregion

watchEffect(async () => {
	const entries = await Promise.all(sortedLocations.value.map(async (dl) => [dl.id, accountsById.value.get(dl.accountId)?.imageUId ? await getAccountImageUrl(accountsById.value.get(dl.accountId).imageUId) : null]));
	avatarUrlByLocationId.value = Object.fromEntries(entries);
});

// #region Methods
function avatarUrlFor(item) {
	return avatarUrlByLocationId.value[item.id] || null;
}

function onFiltersChanged({ filteredLocations: fl, chips }) {
	filteredByDialog.value = fl;
	filterChips.value = chips || [];
}

function removeFilterChip(key) {
	filtersDialogRef.value?.removeFilterValue(key);
}

function resetFilters() {
	filtersDialogRef.value?.clearFilters();
}

function onFilterChipSelect() {
	filtersDialogRef.value?.open();
}

function selectLocation(dl) {
	selected.value = selected.value?.id === dl.id ? null : dl;
}

function confirmSelection() {
	currentParams.value.sender?.__modalDLSelected?.(selected.value, currentParams.value.sender);
	close();
}

function close() {
	openDialog.value = false;
	selected.value = null;
	searchText.value = '';
	//filteredByDialog.value = null;
	currentParams.value = {};
	restrictedLocationIds.value = null;
	window.dispatchEvent(new CustomEvent('vue-delivery-location-modal-closed'));
}

// replaces deliverylocationVM.prototype.evtModalDL — triggered by iframeWatcher.js
// via window.dispatchEvent(new CustomEvent('open-vue-delivery-location-modal', { detail: params }))
function handleOpen(evt) {
	const params = evt.detail || {};
	currentParams.value = params;

	const authorizedDLT = params.authorizedDLT?.length ? params.authorizedDLT : ecosVM.deliveryLocationTypes.map((dlt) => dlt.id);
	deliveryLocationModalType.value = ecosVM.deliveryLocationTypes.find((dlt) => dlt.reference === 'STORE' && authorizedDLT.includes(dlt.id)) || ecosVM.deliveryLocationTypes.find((dlt) => authorizedDLT.includes(dlt.id));
	restrictedLocationIds.value = params.locationIds?.length ? new Set(params.locationIds) : null;

	openDialog.value = true;
}

function onDeliveryLocationCreated(newDl) {
	//refresh ecosVM
	if (!ecosVM.deliveryLocations.includes(newDl.result)) {
		ecosVM.deliveryLocations.push(newDl.result);
	}
	deliveryLocations.value = [...ecosVM.deliveryLocations];
}
//#endregion

// #region Lifecycle Hooks
onMounted(() => {
	window.addEventListener('open-vue-delivery-location-modal', handleOpen);
	amplify.subscribe('DeliveryLocationCreated', onDeliveryLocationCreated);
});

onBeforeUnmount(() => {
	window.removeEventListener('open-vue-delivery-location-modal', handleOpen);
	amplify.unsubscribe('DeliveryLocationCreated', onDeliveryLocationCreated);
});
//#endregion
</script>

<style>
.delivery-location-dialog {
	.v-input {
		flex: 1 !important;
	}

	.v-badge__wrapper .v-btn--icon {
		margin-top: 2px !important;
	}

	.sort-by-control {
		width: unset;
	}

	.v-card-title {
		background-color: rgb(var(--v-theme-surface-variant));
		padding: 0.5rem 1rem !important;
	}

	.v-img__img--cover {
		object-fit: contain !important;
	}

	.dl-shared-icon {
		width: 20px;
		height: 20px;
		font-size: 14px;
		border-radius: 50%;
		background-color: #e0f7fa;
		color: #1c79bb;
		margin-right: 4px;
		vertical-align: middle;
	}
}
</style>
